import type { CoordEntry, CoordItem } from "../../types";
import { normalizeFullCoordinateOwnershipId } from "../../services/aipriDb";

export type CatalogListRow = {
  key: string;
  kind: "item" | "set" | "full";
  label: string;
  items: CoordItem[];
  qr: string;
  id: string;
  point: string;
  category: string | null;
  brand: string;
};

function sameItem(left: CoordItem, right: CoordItem): boolean {
  return left === right || (left.id === right.id && left.url === right.url);
}

function fullCoordinateRepresentativeItem(row: CatalogListRow): CoordItem | undefined {
  return row.items.find((item) => item.term === "トップス")
    ?? row.items.find((item) => item.term === "ワンピ");
}

function itemRow(item: CoordItem): CatalogListRow {
  return {
    key: `${item.id}\n${item.url}`,
    kind: "item",
    label: item.term,
    items: [item],
    qr: item.qr ?? "",
    id: item.id,
    point: item.point,
    category: item.category,
    brand: item.brand,
  };
}

export function buildCatalogListRows(entry: CoordEntry): CatalogListRow[] {
  const isOaCategory = entry.category?.toLowerCase().startsWith("oa") === true;
  const isFullCoordinate = isOaCategory && entry.items.some((item) => /^OAF/i.test(item.id));
  if (isFullCoordinate) {
    const qr = entry.items.find((item) => item.setQR)?.setQR
      ?? entry.items.find((item) => item.qr)?.qr
      ?? "";
    return [{
      key: `${entry.key}\nfull-coordinate`,
      kind: "full",
      label: "フルコーデ",
      items: entry.items,
      qr,
      id: entry.items.map((item) => item.id).join("_"),
      point: "",
      category: entry.category,
      brand: entry.brand,
    }];
  }

  const tops = entry.items.find((item) => item.term === "トップス");
  const bottoms = entry.items.find((item) => item.term === "ボトムス");
  if (!isOaCategory || !tops || !bottoms) return entry.items.map(itemRow);

  const setQr = tops.setQR || bottoms.setQR || "";
  const setRow: CatalogListRow = {
    key: `${tops.id}\n${bottoms.id}\nset`,
    kind: "set",
    label: "セット",
    items: [tops, bottoms],
    qr: setQr,
    id: `${tops.id}_${bottoms.id}`,
    point: "",
    category: entry.category,
    brand: entry.brand,
  };
  return [setRow, ...entry.items.filter((item) => item !== tops && item !== bottoms).map(itemRow)];
}

export function hasCompleteEntryQr(rows: CatalogListRow[]): boolean {
  return rows.length > 0 && rows.every((row) => Boolean(row.qr));
}

export function isCatalogListRowOwned(row: CatalogListRow): boolean {
  if (row.kind === "full") return row.items.some((item) => item.owned === true);
  return row.items.length > 0 && row.items.every((item) => item.owned === true);
}

export function ownershipRequestIds(row: CatalogListRow): string[] {
  if (row.kind === "full") {
    const id = row.items.find((item) => /^OAF/i.test(item.id))?.id ?? row.items[0]?.id ?? "";
    return id ? [normalizeFullCoordinateOwnershipId(id)] : [];
  }
  return row.items.map((item) => item.id).filter(Boolean);
}

export type QrRequestTarget = {
  id: string;
  field: "qr" | "setQR";
};

export function qrRequestTarget(row: CatalogListRow): QrRequestTarget | null {
  if (row.kind === "set") return row.id ? { id: row.id, field: "setQR" } : null;
  if (row.kind === "full") {
    const tops = row.items.find((item) => item.term === "トップス");
    const bottoms = row.items.find((item) => item.term === "ボトムス");
    if (tops && bottoms) return { id: `${tops.id}_${bottoms.id}`, field: "setQR" };
    const item = row.items.find((candidate) => candidate.term === "ワンピ") ?? row.items[0];
    return item?.id ? { id: item.id, field: "qr" } : null;
  }
  return row.id ? { id: row.id, field: "qr" } : null;
}

export function findCatalogListRow(entry: CoordEntry, item: CoordItem): CatalogListRow | undefined {
  return buildCatalogListRows(entry).find((row) => {
    if (!row.items.some((candidate) => sameItem(candidate, item))) return false;
    if (row.kind !== "full") return true;
    const representative = fullCoordinateRepresentativeItem(row);
    return representative ? sameItem(representative, item) : false;
  });
}

export function canManageCatalogItem(entry: CoordEntry, item: CoordItem): boolean {
  const fullRow = buildCatalogListRows(entry).find((row) => row.kind === "full");
  if (fullRow) {
    const representative = fullCoordinateRepresentativeItem(fullRow);
    return representative ? sameItem(representative, item) : false;
  }
  const row = findCatalogListRow(entry, item);
  if (row?.kind !== "set") return true;
  const tops = row.items.find((candidate) => candidate.term === "トップス") ?? row.items[0];
  return tops ? sameItem(tops, item) : false;
}

export function catalogItemDisplayStatus(entry: CoordEntry, item: CoordItem): {
  owned: boolean;
  qr: string;
  qrLabel: string;
} {
  const row = findCatalogListRow(entry, item);
  if (!row) return { owned: item.owned === true, qr: item.qr ?? "", qrLabel: item.term };
  return {
    owned: row.kind === "item" ? item.owned === true : isCatalogListRowOwned(row),
    qr: row.qr,
    qrLabel: row.label,
  };
}
